/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosEnumEdsBackgroundColors } from "@cosmos/hygraph-schema";
import { css, cva, cx } from "@panda/css";
import { Box, BoxProps } from "@panda/jsx";
import { token } from "@panda/tokens";
import type { CSSProperties } from "react";
import { useMemo } from "react";
import { Animate, CosmosAsset, OptimizedCosmosAssetFragment } from "..";
import { AttributionStack } from "../attribution/CosmosTagsAndAttribution.atom";
import type { AnimationNames } from "../lottie-animation";
import { LottieAnimation, stretchLottieSetting } from "../lottie-animation";

export interface BackgroundContainerProps extends BoxProps {
	children?: React.ReactNode;
	className?: string;
	style?: CSSProperties;
	bgImageAsset?: OptimizedCosmosAssetFragment | null;
	bgBlur?: boolean;
	edsBackgroundColor?: CosmosEnumEdsBackgroundColors | null;
	bgAnimation?: AnimationNames | null;
	bgFullHeight?: boolean | null;
	bgContain?: boolean | null;
	noGradient?: boolean;
	noAttribution?: boolean;
}

interface CssVariables extends CSSProperties {
	"--background-theme-color"?: string;
}

const boxStyles = css({
	position: "absolute",
	inset: "0",
	maxHeight: "100vh",
});

const lottieStyles = css({
	"& svg": {
		position: "relative",
		width: "100%",
		maxWidth: "1920px",
		marginInline: "auto",
	},
});

const imageLayerStyles = css({
	zIndex: 0,
});

const imageAssetStyles = cva({
	base: {
		width: "100%",
		height: "100%",
	},
	variants: {
		layout: {
			cover: {
				objectFit: "cover",
				objectPosition: "top",
			},
			coverCenter: {
				objectFit: "cover",
				objectPosition: "center",
			},
			contain: {
				objectFit: "contain",
				objectPosition: "top center",
				maxWidth: "clamp(915px, 100%, 1200px)",
				marginInline: "auto",
			},
		},
	},
	defaultVariants: {
		layout: "cover",
	},
});
type AssetLayoutsTypes = (typeof imageAssetStyles.variantMap)["layout"][number];

const gradientOverlayStyles = cva({
	base: {
		pointerEvents: "none",
	},
	variants: {
		type: {
			textShade: {
				background: "linear-gradient(270deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.75) 100%)",
			},
			fade: {
				background: "linear-gradient(180deg, rgba(16, 16, 20, 0.00), var(--background-theme-color) 50%)",
			},
			none: {},
		},
	},
	defaultVariants: {
		type: "none",
	},
});
type GradientOverlayTypes = (typeof gradientOverlayStyles.variantMap)["type"][number];

const primaryTextColorStyles = css({
	"& .section-heading .cosmos-rich-text": {
		color: "eds.text.primary",
	},
});

const blurContainerAnimationStyles = css({
	backdropFilter: "blur(100px)",
	"@supports (backdrop-filter: blur(min(8vw, 100px)))": {
		backdropFilter: "blur(min(8vw, 100px))",
	},
});

const blurContainerDefaultStyles = css({
	backdropFilter: "var(--effect-material-blur, blur(50px))",
});

export const BackgroundContainer = ({
	className = "",
	style,
	children,
	edsBackgroundColor,
	bgImageAsset,
	bgBlur,
	bgAnimation,
	bgFullHeight,
	bgContain,
	noGradient,
	noAttribution,
	...rest
}: BackgroundContainerProps): React.ReactNode => {
	// Background layers from back to front:
	// 1. Theme color background
	// 2. Image/Video layer (if present)
	// 3. Animation layer
	// 4. Blur layer
	// 5. Gradient overlay
	// 6. Attribution layer (bottom right overlay)
	// 7. Content

	const hasAsset = !!bgImageAsset;
	const hideGradient = noGradient || !hasAsset || bgContain;
	const gradientOverlayType: GradientOverlayTypes = hideGradient ? "none" : bgFullHeight ? "textShade" : "fade";
	const imageLayoutType: AssetLayoutsTypes = bgContain ? "contain" : bgFullHeight ? "coverCenter" : "cover";
	const themeStyle = useMemo(
		() =>
			({
				"--background-theme-color": token(
					`colors.eds.background.${edsBackgroundColor === CosmosEnumEdsBackgroundColors.ElevatedLowDefault ? "elevated.low.default" : "default"}`,
				),
			}) as CssVariables,

		[edsBackgroundColor],
	);

	if (!bgImageAsset && !edsBackgroundColor && !bgAnimation && !style) {
		return children;
	}

	return (
		<Box
			className={cx(
				"theme-container",
				edsBackgroundColor || "",
				bgImageAsset ? primaryTextColorStyles : "",
				className || "",
			)}
			style={{
				background: "var(--background-theme-color)",
				position: bgBlur || bgAnimation || bgImageAsset ? "relative" : undefined,
				...themeStyle,
				...style,
			}}
			{...rest}
		>
			{/* Image/Video Layer */}
			{bgImageAsset ? (
				<Box className={cx("bg-image-container", boxStyles, imageLayerStyles)}>
					<CosmosAsset asset={bgImageAsset} hideAttribution className={imageAssetStyles({ layout: imageLayoutType })} />
				</Box>
			) : null}

			{/* Animation Layer */}
			{bgAnimation ? (
				<Animate delay={0.35} type="fade">
					<LottieAnimation
						className={cx("lottie-container", boxStyles, lottieStyles)}
						lottie={bgAnimation}
						rendererSettings={stretchLottieSetting}
					/>
				</Animate>
			) : null}

			{/* Blur Layer */}
			{bgBlur || bgAnimation ? (
				<Box
					className={cx(
						"theme-blur-container",
						boxStyles,
						bgAnimation ? blurContainerAnimationStyles : blurContainerDefaultStyles,
					)}
				></Box>
			) : null}

			{/* Gradient Overlay */}
			{gradientOverlayType !== "none" ? (
				<Box className={cx("bg-gradient-overlay", boxStyles, gradientOverlayStyles({ type: gradientOverlayType }))} />
			) : null}

			{/* Attribution Stack Layer */}
			{!noAttribution && bgImageAsset?.attribution ? (
				<AttributionStack attribution={bgImageAsset?.attribution} className="bg-attribution-stack" />
			) : null}

			{/* Content */}
			{children}
		</Box>
	);
};
